import { useCallback, useLayoutEffect, useRef } from "react";

import {
	focusListRestoreAnchor,
	scrollAnchorIntoSavedPosition,
	tableAnchorSelector,
} from "../utils/scroll-container";

const SCROLL_RETRY_FRAMES = 30;

/**
 * After list data is ready, scroll the anchored row into the viewport offset
 * it had when the user left. Retries across frames because React Aria rows
 * often commit `data-list-anchor` after the first layout.
 *
 * A missing DOM node is not AC8 by itself — the table may still be hidden
 * while a restored page loads. AC8 (`onAnchorMissing`) runs only when the
 * caller knows the id is not in the current items.
 */
export const useRestoreAnchorScroll = ({
	anchorId,
	anchorOffset,
	anchorViaKeyboard,
	enabled,
	isAnchorInItems = true,
	isReady,
	onAnchorConsumed,
	onAnchorMissing,
	selector,
}: {
	anchorId?: string;
	anchorOffset?: number;
	/** When true, restore keyboard focus to the anchor after scrolling. */
	anchorViaKeyboard?: boolean;
	enabled: boolean;
	/** False when this page's data does not include the clicked row. */
	isAnchorInItems?: boolean;
	isReady: boolean;
	/**
	 * The saved row has been used up — either scrolled to, or found to be gone.
	 *
	 * Real-world case: you return to the alert you clicked, then keep scrolling
	 * and leave via the sidebar. Without this, we would save the old row and its
	 * old screen position again, so the *next* Back would yank you to where you
	 * were two visits ago instead of where you actually stopped.
	 *
	 * Must be a stable callback — it is an effect dependency.
	 */
	onAnchorConsumed?: () => void;
	onAnchorMissing?: () => void;
	selector: (id: string) => string;
}): void => {
	/**
	 * Which row we have already restored, rather than a plain "have we restored
	 * yet" flag.
	 *
	 * A list can be swapped in place without unmounting — the same table pointed
	 * at a different location's sensors, say. A one-way latch would put that
	 * second list on the right page but never scroll it to the right row, and
	 * the failure is silent. Keying on the selector scopes this per list *and*
	 * per row, since table selectors carry the `listKey`.
	 */
	const restoredKeyRef = useRef<string | undefined>(undefined);

	useLayoutEffect(() => {
		if (!enabled || !isReady || !anchorId) {
			return;
		}

		const restoreKey = selector(anchorId);
		if (restoredKeyRef.current === restoreKey) {
			return;
		}

		const tryScroll = (): boolean => {
			const element = document.querySelector<HTMLElement>(restoreKey);
			if (!element) {
				return false;
			}

			restoredKeyRef.current = restoreKey;
			scrollAnchorIntoSavedPosition(element, anchorOffset);
			if (anchorViaKeyboard) {
				focusListRestoreAnchor(element);
			}
			onAnchorConsumed?.();
			return true;
		};

		if (tryScroll()) {
			return;
		}

		if (!isAnchorInItems) {
			restoredKeyRef.current = restoreKey;
			onAnchorMissing?.();
			onAnchorConsumed?.();
			return;
		}

		let frames = 0;
		let raf = 0;
		const tick = () => {
			if (restoredKeyRef.current === restoreKey) {
				return;
			}
			frames += 1;
			if (tryScroll()) {
				return;
			}
			if (frames < SCROLL_RETRY_FRAMES) {
				raf = requestAnimationFrame(tick);
			}
		};
		raf = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(raf);
		};
	}, [
		anchorId,
		anchorOffset,
		anchorViaKeyboard,
		enabled,
		isAnchorInItems,
		isReady,
		onAnchorConsumed,
		onAnchorMissing,
		selector,
	]);
};

/** Table rows use scoped `data-list-anchor` from `listRestoreAnchorRowProps`. */
export const useRestoreTableAnchorScroll = (options: {
	anchorId?: string;
	anchorOffset?: number;
	anchorViaKeyboard?: boolean;
	enabled: boolean;
	isAnchorInItems?: boolean;
	isReady: boolean;
	listKey: string;
	onAnchorConsumed?: () => void;
	onAnchorMissing?: () => void;
}): void => {
	const { listKey, ...rest } = options;
	const selector = useCallback(
		(id: string) => tableAnchorSelector(listKey, id),
		[listKey],
	);
	useRestoreAnchorScroll({
		...rest,
		selector,
	});
};
